// src/context/AuthContext.jsx
// NOTE: Spark plan mode (no Cloud Functions)
// ------------------------------------------
// Invite/approval test mode:
// User must be logged in AND approved in Firestore to access the private site.
//
// Approved user document example:
// users/{uid}
//   approved: true

import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { auth, db, IS_EMU } from "@/firebase/firebase.jsx";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";

import {
    registerSessionForUser,
    markSessionSignedOut,
} from "../translations/utils/sessionTracker.js";

const Ctx = createContext({
    user: null,
    currentUser: null,
    roles: [],
    claims: {},
    approved: false,
    loading: true,
});

function normalizeRoles(input) {
    if (!input) return [];

    if (Array.isArray(input)) return input.filter(Boolean).map((v) => String(v).trim());

    if (typeof input === "string") {
        const raw = String(input).trim();
        if (!raw) return [];

        const cleaned = raw.replace(/[\[\]]/g, "");
        const parts = cleaned
            .split(",")
            .map((p) => p.trim().replace(/^["']|["']$/g, ""))
            .filter(Boolean);

        return parts.length ? parts : [raw.replace(/^["']|["']$/g, "")];
    }

    if (typeof input === "object") {
        return Object.keys(input).filter((k) => !!input[k]);
    }

    return [];
}

function normalizeEmail(v) {
    return String(v || "").trim().toLowerCase();
}

function sortRoles(list) {
    const priority = ["admin", "moderator", "owner", "businessOwner", "government", "public"];
    const p = new Map(priority.map((r, i) => [r, i]));
    return [...new Set(list)]
        .filter(Boolean)
        .map((v) => String(v).trim())
        .sort((a, b) => (p.get(a) ?? 999) - (p.get(b) ?? 999));
}

function isApprovedUser(userData, rolesList = []) {
    if (!userData) return false;

    if (userData.approved === true) return true;
    if (userData.betaApproved === true) return true;
    if (userData.accessApproved === true) return true;
    if (userData.inviteApproved === true) return true;

    // Safety for your own admin account during testing
    if (rolesList.includes("admin")) return true;

    return false;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [claims, setClaims] = useState({});
    const [roles, setRoles] = useState(["public"]);
    const [approved, setApproved] = useState(false);
    const [loading, setLoading] = useState(true);

    const rolesRequestIdRef = useRef(0);

    const loadUserProfile = async (u) => {
        if (!u) return null;

        const uid = u.uid;
        const emailNorm = normalizeEmail(u.email);

        let userData = null;

        const uidDocRef = doc(db, "users", uid);
        const uidSnap = await getDoc(uidDocRef);

        console.log("[Auth] uid:", uid, "email:", emailNorm);
        console.log("[Auth] users/{uid} exists?", uidSnap.exists());

        if (uidSnap.exists()) {
            userData = uidSnap.data();
            console.log(
                "[Auth] users/{uid} data roles:",
                userData?.roles,
                "role:",
                userData?.role,
                "approved:",
                userData?.approved,
                "email:",
                userData?.email
            );
        } else if (u.email) {
            console.log("[Auth] users/{uid} missing. Trying email fallback:", emailNorm);

            const q = query(collection(db, "users"), where("email", "==", emailNorm));
            const qSnap = await getDocs(q);

            console.log("[Auth] email fallback docs found:", qSnap.size);

            if (!qSnap.empty) {
                const preferred = qSnap.docs.find((d) => d.id === uid) || qSnap.docs[0];
                console.log("[Auth] email fallback using doc id:", preferred.id);

                userData = preferred.data();
                console.log(
                    "[Auth] email fallback data roles:",
                    userData?.roles,
                    "role:",
                    userData?.role,
                    "approved:",
                    userData?.approved,
                    "email:",
                    userData?.email
                );
            }
        }

        return userData;
    };

    const deriveRolesAndApproval = async (u, c = {}) => {
        const s = new Set();

        normalizeRoles(c.roles).forEach((r) => s.add(r));
        normalizeRoles(c.role).forEach((r) => s.add(r));
        if (c.admin) s.add("admin");
        if (c.government) s.add("government");
        if (c.owner) s.add("owner");

        let userData = null;

        if (u) {
            try {
                userData = await loadUserProfile(u);

                if (userData) {
                    normalizeRoles(userData.roles).forEach((r) => s.add(r));
                    normalizeRoles(userData.role).forEach((r) => s.add(r));
                }
            } catch (err) {
                console.warn("AuthContext: failed to load Firestore user profile", err?.code || err);
            }
        }

        s.add("public");

        const resultRoles = sortRoles(Array.from(s));
        const resultApproved = isApprovedUser(userData, resultRoles);

        console.log("[Auth] roles resolved:", resultRoles);
        console.log("[Auth] approved resolved:", resultApproved);

        return { roles: resultRoles, approved: resultApproved };
    };

    const refreshClaims = async (u, options = {}) => {
        if (!u) {
            setApproved(false);
            return ["public"];
        }

        const currentRequestId = ++rolesRequestIdRef.current;

        try {
            const { forceRefresh = false } = options;
            const res = await u.getIdTokenResult(forceRefresh);
            const c = res?.claims || {};

            if (rolesRequestIdRef.current !== currentRequestId) return ["public"];

            setClaims(c);

            const result = await deriveRolesAndApproval(u, c);

            if (rolesRequestIdRef.current !== currentRequestId) return result.roles;

            setRoles(result.roles);
            setApproved(result.approved);

            return result.roles;
        } catch (err) {
            console.warn("AuthContext: refreshClaims failed", err?.code || err);

            if (rolesRequestIdRef.current === currentRequestId) {
                setClaims({});
                setRoles(["public"]);
                setApproved(false);
            }

            return ["public"];
        }
    };

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (u) => {
            setUser(u || null);

            console.log("[Auth] onAuthStateChanged user:", u ? { uid: u.uid, email: u.email } : null);

            if (!u) {
                rolesRequestIdRef.current += 1;

                try {
                    await markSessionSignedOut();
                } catch (err) {
                    console.warn("Failed to mark session signed out", err);
                }

                setClaims({});
                setRoles(["public"]);
                setApproved(false);
                setLoading(false);
                return;
            }

            setLoading(false);

            refreshClaims(u).catch((err) => {
                console.warn("Background refreshClaims failed", err?.code || err);
            });

            registerSessionForUser(u).catch((err) => {
                console.warn("Failed to register session", err);
            });
        });

        return () => {
            rolesRequestIdRef.current += 1;
            unsub && unsub();
        };
    }, []);

    const value = useMemo(
        () => ({
            user,
            currentUser: user,
            roles,
            claims,
            approved,
            loading,
            refreshClaims,
            signOut: () => signOut(auth),
            isAdmin: roles.includes("admin"),
            isOwner: roles.includes("owner") || roles.includes("businessOwner"),
            isGov: roles.includes("government"),
            isEmulator: IS_EMU,
        }),
        [user, roles, claims, approved, loading]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
    return useContext(Ctx);
}